import { IntlShape } from 'react-intl';
import {
  parseApiResponseError,
  ApiErrorCodes,
} from '../../../../api/api-error';
import {
  PersonaFisica,
  PersonaGiuridica,
  PersonaTipoUtenteEnum,
  UtentiError,
  UtentiErrorCodiciErroreEnum,
} from '../../../../api/generated/apc/api';
import { env } from '../../../../env';
import commonErrorMessages from '../../../../messages/common-messages/common-error.messages';
import emailVerificationUserRegistrationMessages from '../../../../messages/user-access/user-registration-messages/emailVerificationUserRegistration.messages';
import insertEmailUserRegistrationMessages from '../../../../messages/user-access/user-registration-messages/insertEmailUserRegistration.messages';
import insertFiscalCodeUserRegistrationMessages from '../../../../messages/user-access/user-registration-messages/insertFiscalCodeUserRegistration.messages';
import insertPhoneNumberUserRegistrationMessages from '../../../../messages/user-access/user-registration-messages/insertPhoneNumberUserRegistration.messages';
import phoneVerificationUserRegistrationMessages from '../../../../messages/user-access/user-registration-messages/phoneVerificationUserRegistration.messages';
import userRegistrationMessages from '../../../../messages/user-access/user-registration-messages/userRegistration.messages';
import { ErrorMessages, FormattedErrorMessages } from '../../../../model/error';
import {
  isEmailValid,
  isFiscalCodeValid,
  isPhoneNumberValid,
  isUserCodeValid,
  isVatCodeFiscalCodeValid,
} from '../../../../utils/validators';
import {
  InsertEmailError,
  InsertFiscalCodeLegalPersonError,
  InsertPhoneNumberError,
  PrivacyAgreementError,
  SetupAccountError,
  UserRegistrationState,
  VerificationEmailError,
  VerificationPhoneNumberError,
} from './user-registration-types';

export const verifySetupAccountFields = (
  state: UserRegistrationState
): SetupAccountError | undefined => {
  const { selectedOptionUserType, userCode, fiscalCode, vatCodeFiscalCode , fiduciaria} = 
    state;
  if (
    (selectedOptionUserType === PersonaTipoUtenteEnum.Pf && !userCode) ||
    (selectedOptionUserType === PersonaTipoUtenteEnum.Pf && !fiscalCode)
  ) {
    return 'FIELDS_NOT_FILL';
  } else if (
    (selectedOptionUserType === PersonaTipoUtenteEnum.Pg && !userCode) ||
    (selectedOptionUserType === PersonaTipoUtenteEnum.Pg && !vatCodeFiscalCode)
  ) {
    return 'FIELDS_NOT_FILL';
  } else if (!isUserCodeValid(userCode)) {
    return 'INVALID_CU';
  } else if (
    selectedOptionUserType === PersonaTipoUtenteEnum.Pf &&
    !isFiscalCodeValid(fiscalCode)
  ) {
    return 'INVALID_CF';
  } else if (
    selectedOptionUserType === PersonaTipoUtenteEnum.Pg &&
    !isVatCodeFiscalCodeValid(vatCodeFiscalCode) &&
    !fiduciaria
  ) {
    return 'INVALID_PI';
  } else if (
    selectedOptionUserType === PersonaTipoUtenteEnum.Pg &&
    !isVatCodeFiscalCodeValid(vatCodeFiscalCode) &&
    fiduciaria) {
    return 'INVALID_FI';
  } else {
    return undefined;
  }
};

export const messagesForSetupAccountError = (
  error: SetupAccountError
): ErrorMessages => {
  switch (error) {
    case 'ATTIVAZIONE_IMPOSSIBILE':
      return {
        title: userRegistrationMessages.errorImpossibleActivation,
      };
    case 'CF_NON_ASSOCIATO':
      return {
        title: userRegistrationMessages.errorCfNonAssociato,
      };
    case 'CF_NON_ASSOCIATO_FIDUCIARIA':
      return {
        title: userRegistrationMessages.errorCfNonAssociatoFiduciaria,
      };
    case 'P_IVA_NON_ASSOCIATA':
      return {
        title: userRegistrationMessages.errorPivaNonAssociata,
      };
    case 'UTENTE_INESISTENTE':
      return {
        title: userRegistrationMessages.errorUtenteInesistente,
      };
    case 'UTENTE_NON_VALIDO':
      return {
        title: userRegistrationMessages.errorUtenteNonValido,
      };
    case 'DOCUMENTO_NON_AMMESSO':
      return {
        title: userRegistrationMessages.errorDocumentNotAdmitted,
      };
    case 'DOCUMENTO_SCADUTO':
      return {
        title: userRegistrationMessages.errorExpiredDocument,
      };
    case 'VINCOLI_PRESENTI':
      return {
        title: userRegistrationMessages.errorPersonalDataConstraints,
      };
    case 'UTENTE_GIA_REGISTRATO':
      return {
        title: userRegistrationMessages.errorUserAlreadyRegistered,
      };
    case 'INVALID_CU':
      return {
        title: userRegistrationMessages.errorInvalidCU,
      };
    case 'INVALID_CF':
      return {
        title: userRegistrationMessages.errorInvalidCF,
      };
    case 'INVALID_PI':
      return {
        title: userRegistrationMessages.errorInvalidPI,
      };
    case 'INVALID_FI':
      return {
        title: userRegistrationMessages.errorInvalidFI,
      };
    case 'FIELDS_NOT_FILL':
      return {
        title: commonErrorMessages.errorFieldsNotFill,
      };
    case 'UTENZA_NON_MIGRATA':
      return {
        title:
          env.appModule === 'web'
            ? commonErrorMessages.errorNotMigratedUserWeb
            : commonErrorMessages.errorNotMigratedUserMobile,
      };
    case 'GENERIC_ERROR':
    default:
      return {
        title: commonErrorMessages.errorTitleDefault,
        description: commonErrorMessages.errorDescriptionDefault,
      };
  }
};

export const messagesForPrivacyAgreementError = (
  error: PrivacyAgreementError
): ErrorMessages => {
  switch (error) {
    case 'GENERIC_ERROR':
    default:
      return {
        title: commonErrorMessages.errorTitleDefault,
        description: commonErrorMessages.errorDescriptionDefault,
      };
  }
};

export const verifyInsertEmailFields = (
  state: UserRegistrationState
): InsertEmailError | undefined => {
  const { email } = state;

  if (!isEmailValid(email)) {
    return 'INVALID_EMAIL_FORMAT';
  }
  return undefined;
};

export const messagesForInsertEmailError = (
  error: InsertEmailError
): ErrorMessages => {
  switch (error) {
    case 'INVALID_EMAIL_FORMAT':
      return {
        title: insertEmailUserRegistrationMessages.errorInvalidEmailFormat,
      };
    case 'EMAIL_NON_UTILIZZABILE':
      return {
        title: insertEmailUserRegistrationMessages.errorEmailNotUsableMessages,
      };
    case 'GENERIC_ERROR':
    default:
      return {
        title: commonErrorMessages.errorTitleDefault,
        description: commonErrorMessages.errorDescriptionDefault,
      };
  }
};

export const messagesForVerificationEmailError = (
  error: VerificationEmailError,
  intl?: IntlShape
): FormattedErrorMessages => {
  const { formatMessage } = intl;
  switch (error.type) {
    case 'ACCORDO_QUADRO_NON_ATTIVO':
      return {
        title: formatMessage(
          phoneVerificationUserRegistrationMessages.errorAccordoQuadroNonAttivo
        ),
      };
    case 'CODICE_DI_VERIFICA_SBAGLIATO':
      return {
        title: formatMessage(
          emailVerificationUserRegistrationMessages.errorWrongCode,
          {
            attempts: error.attempts,
          }
        ),
      };
    case 'GENERIC_ERROR':
    default:
      return {
        title: formatMessage(commonErrorMessages.errorTitleDefault),
        description: formatMessage(commonErrorMessages.errorDescriptionDefault),
      };
  }
};

export const verifyInsertPhoneNumberFields = (
  state: UserRegistrationState
): InsertPhoneNumberError | undefined => {
  const { phoneNumber } = state;

  if (!isPhoneNumberValid(phoneNumber)) {
    return 'INVALID_PHONE_NUMBER_FORMAT';
  }
  return undefined;
};

export const verifyInsertSecondaryPhoneNumberFields = (
  state: UserRegistrationState
): InsertPhoneNumberError | undefined => {
  const { phoneNumber, secondaryPhoneNumber } = state;

  if (!isPhoneNumberValid(secondaryPhoneNumber)) {
    return 'INVALID_PHONE_NUMBER_FORMAT';
  }
  if (secondaryPhoneNumber === phoneNumber) {
    return 'PHONE_ALREADY_USED';
  }
  return undefined;
};

export const messagesForInsertPhoneNumberError = (
  error: InsertPhoneNumberError
): ErrorMessages => {
  switch (error) {
    case 'INVALID_PHONE_NUMBER_FORMAT':
      return {
        title:
          insertPhoneNumberUserRegistrationMessages.errorInvalidPhoneNumberFormat,
      };
    case 'CELLULARE_NON_UTILIZZABILE':
      return {
        title:
          insertPhoneNumberUserRegistrationMessages.errorPhoneNumberNotUsableMessages,
      };
    case 'PHONE_ALREADY_USED':
      return {
        title:
          insertPhoneNumberUserRegistrationMessages.errorPhoneNumberAlreadyUsedMessages,
      };
    case 'GENERIC_ERROR':
    default:
      return {
        title: commonErrorMessages.errorTitleDefault,
        description: commonErrorMessages.errorDescriptionDefault,
      };
  }
};

export const messagesForVerificationPhoneNumberError = (
  error: VerificationPhoneNumberError,
  intl?: IntlShape
): FormattedErrorMessages => {
  const { formatMessage } = intl;
  switch (error.type) {
    case 'ACCORDO_QUADRO_NON_ATTIVO':
      return {
        title: formatMessage(
          phoneVerificationUserRegistrationMessages.errorAccordoQuadroNonAttivo
        ),
      };
    case 'CODICE_DI_VERIFICA_SBAGLIATO':
      return {
        title: formatMessage(
          phoneVerificationUserRegistrationMessages.errorWrongCode,
          {
            attempts: error.attempts,
          }
        ),
      };
    case 'GENERIC_ERROR':
    default:
      return {
        title: formatMessage(commonErrorMessages.errorTitleDefault),
        description: formatMessage(commonErrorMessages.errorDescriptionDefault),
      };
  }
};

export const verifyInsertFiscalCodeLegalPersonFields = (
  state: UserRegistrationState
): InsertFiscalCodeLegalPersonError | undefined => {
  const { fiscalCodeLegalPerson } = state;

  if (!fiscalCodeLegalPerson) {
    return 'FIELD_NOT_FILL';
  }
  if (!isFiscalCodeValid(fiscalCodeLegalPerson)) {
    return 'INVALID_FISCAL_CODE_FORMAT';
  }

  return undefined;
};

export const messagesForInsertFiscalCodeLegalPersonError = (
  error: InsertFiscalCodeLegalPersonError
): ErrorMessages => {
  switch (error) {
    case 'FIELD_NOT_FILL':
      return {
        title: commonErrorMessages.errorFieldsNotFill,
      };
    case 'INVALID_FISCAL_CODE_FORMAT':
      return {
        title:
          insertFiscalCodeUserRegistrationMessages.errorInvalidFiscalCodeFormat,
      };
    case 'ATTIVAZIONE_IMPOSSIBILE':
      return {
        title:
          insertFiscalCodeUserRegistrationMessages.errorImpossibleActivation,
      };
    case 'UTENTE_NON_VALIDO':
      return {
        title: insertFiscalCodeUserRegistrationMessages.errorUtenteNonValido,
      };
    case 'DOCUMENTO_NON_AMMESSO':
      return {
        title:
          insertFiscalCodeUserRegistrationMessages.errorDocumentNotAdmitted,
      };
    case 'DOCUMENTO_SCADUTO':
      return {
        title: insertFiscalCodeUserRegistrationMessages.errorExpiredDocument,
      };
    case 'VINCOLI_PRESENTI':
      return {
        title:
          insertFiscalCodeUserRegistrationMessages.errorPersonalDataConstraints,
      };
    case 'UTENTE_GIA_REGISTRATO':
      return {
        title:
          insertFiscalCodeUserRegistrationMessages.errorUserAlreadyRegistered,
      };
    case 'UTENTE_INESISTENTE':
      return {
        title: insertFiscalCodeUserRegistrationMessages.errorUtenteInesistente,
      };
    case 'CF_NON_ASSOCIATO':
      return {
        title: insertFiscalCodeUserRegistrationMessages.errorCfNonAssociato,
      };
    case 'CF_NON_ASSOCIATO_FIDUCIARIA':
      return {
        title: insertFiscalCodeUserRegistrationMessages.errorCfNonAssociatoFiduciaria,
      };
    case 'P_IVA_NON_ASSOCIATA':
      return {
        title: insertFiscalCodeUserRegistrationMessages.errorPivaNonAssociata,
      };
    case 'CF_NO_FIDUCIARIE':
      return {
        title: insertFiscalCodeUserRegistrationMessages.errorCfNoFiduciarie,
      };
    case 'UTENZA_NON_MIGRATA':
      return {
        title:
          env.appModule === 'web'
            ? commonErrorMessages.errorNotMigratedUserWeb
            : commonErrorMessages.errorNotMigratedUserMobile,
      };
    case 'GENERIC_ERROR':
    default:
      return {
        title: commonErrorMessages.errorTitleDefault,
        description: commonErrorMessages.errorDescriptionDefault,
      };
  }
};

export const parseCreateUserRequest = (
  state: UserRegistrationState
): PersonaFisica | PersonaGiuridica => {
  if (state.selectedOptionUserType === PersonaTipoUtenteEnum.Pf) {
    return {
      tipo: PersonaTipoUtenteEnum.Pf,
      tipoUtente: PersonaTipoUtenteEnum.Pf,
      codiceUtente: state.userCode,
      codiceFiscale: state.fiscalCode,
    } as PersonaFisica;
  } else {
    return {
      tipo: PersonaTipoUtenteEnum.Pg,
      tipoUtente: PersonaTipoUtenteEnum.Pg,
      codiceUtente: state.userCode,
      partitaIva: state.vatCodeFiscalCode,
      fiduciaria: state.fiduciaria,
    } as PersonaGiuridica;
  }
};

export const getSetupAccountError = (error: unknown): SetupAccountError => {
  const apiError = parseApiResponseError<UtentiError>(error);
  const errorCodes = apiError.payload?.codiciErrore;
  const invalidFields = apiError.payload?.campiNonValidi;
  if (apiError.responseErrorCode === ApiErrorCodes.BAD_REQUEST) {
    if (errorCodes?.length > 0) {
      switch (errorCodes[0]) {
        case UtentiErrorCodiciErroreEnum.AttivazioneImpossibile:
          return 'ATTIVAZIONE_IMPOSSIBILE';
        case UtentiErrorCodiciErroreEnum.UtenteInesistente:
          return 'UTENTE_INESISTENTE';
        case UtentiErrorCodiciErroreEnum.CfNonAssociato:
          return 'CF_NON_ASSOCIATO';
        case UtentiErrorCodiciErroreEnum.CfNonAssociatoFiduciaria:
          return 'CF_NON_ASSOCIATO_FIDUCIARIA';
        case UtentiErrorCodiciErroreEnum.PIvaNonAssociata:
          return 'P_IVA_NON_ASSOCIATA';
        case UtentiErrorCodiciErroreEnum.DocumentoNonAmmesso:
          return 'DOCUMENTO_NON_AMMESSO';
        case UtentiErrorCodiciErroreEnum.DocumentoScaduto:
          return 'DOCUMENTO_SCADUTO';
        case UtentiErrorCodiciErroreEnum.VincoliPresenti:
          return 'VINCOLI_PRESENTI';
        case UtentiErrorCodiciErroreEnum.UtenteGiaRegistrato:
          return 'UTENTE_GIA_REGISTRATO';
        case UtentiErrorCodiciErroreEnum.UtenzaNonMigrata:
          return 'UTENZA_NON_MIGRATA';
        case UtentiErrorCodiciErroreEnum.UtenteNonValido:
          return 'UTENTE_NON_VALIDO';
      }
    } else if (invalidFields?.length > 0) {
      switch (invalidFields[0]) {
        case 'codiceFiscale':
          return 'INVALID_CF';
        case 'codiceUtente':
          return 'INVALID_CU';
        case 'partitaIva':
          return 'INVALID_PI';
      }
    }
  }
  return 'GENERIC_ERROR';
};
